import { useState, useCallback, useMemo, useEffect } from 'react'
import type { Mint } from '../types'

interface UseSwapFormProps {
  availableMints: Mint[]
  withdrawalAddresses: any[]
  allWithdrawalAddresses: any[]
  balanceData: any
}

export function useSwapForm({
  availableMints,
  withdrawalAddresses,
  allWithdrawalAddresses,
  balanceData,
}: UseSwapFormProps) {
  // Form state
  const [fromMintId, setFromMintId] = useState<string>('')
  const [toMintId, setToMintId] = useState<string>('')
  const [fromAmount, setFromAmount] = useState<string>('')
  const [selectedAddressId, setSelectedAddressId] = useState<string>('')
  const [selectedPercentage, setSelectedPercentage] = useState<number>(100)

  // Computed values
  const fromMintBalance = useMemo(() => {
    if (!fromMintId || !balanceData?.getCompanyBalance.holdings) return '0'
    const holding = balanceData.getCompanyBalance.holdings.find(
      (h: any) => h.mint.id === fromMintId,
    )
    return holding?.balance || '0'
  }, [fromMintId, balanceData])

  const isAmountGreaterThanBalance = useMemo(() => {
    // Remove commas from formatted balance string before parsing
    return parseFloat(fromAmount) > parseFloat(fromMintBalance.replace(/,/g, ''))
  }, [fromAmount, fromMintBalance])

  const fromMint = useMemo(
    () => availableMints.find((mint) => mint.id === fromMintId),
    [availableMints, fromMintId],
  )

  const toMint = useMemo(
    () => availableMints.find((mint) => mint.id === toMintId),
    [availableMints, toMintId],
  )

  useEffect(() => {
    setSelectedAddressId('')
  }, [toMintId])

  const filteredWithdrawalAddresses = useMemo(() => {
    if (!toMint || toMint.chain === 'FIAT') return []
    return withdrawalAddresses.filter((addr) => addr.chain === toMint.chain)
  }, [withdrawalAddresses, toMint])

  // 'none' = no addresses at all, 'pending' = only pending addresses exist, 'ok' = has approved addresses
  const withdrawalAddressStatus = useMemo((): 'none' | 'pending' | 'ok' => {
    if (!toMint || toMint.chain === 'FIAT') return 'ok'
    const allForChain = allWithdrawalAddresses.filter((addr) => addr.chain === toMint.chain)
    if (allForChain.length === 0) return 'none'
    if (filteredWithdrawalAddresses.length > 0) return 'ok'
    return 'pending'
  }, [toMint, allWithdrawalAddresses, filteredWithdrawalAddresses])

  const handlePercentageClick = useCallback(
    (percentage: number) => {
      // Remove commas from formatted balance string before parsing
      const balance = parseFloat(fromMintBalance.replace(/,/g, ''))
      if (isNaN(balance)) return
      const amount = balance * (percentage / 100)
      setFromAmount(amount.toFixed(8).replace(/\.?0+$/, ''))
      setSelectedPercentage(percentage)
    },
    [fromMintBalance],
  )

  const resetForm = useCallback(() => {
    setFromMintId('')
    setToMintId('')
    setFromAmount('')
    setSelectedAddressId('')
    setSelectedPercentage(100)
  }, [])

  return {
    // State
    fromMintId,
    setFromMintId,
    toMintId,
    setToMintId,
    fromAmount,
    setFromAmount,
    selectedAddressId,
    setSelectedAddressId,
    selectedPercentage,
    setSelectedPercentage,

    // Computed values
    fromMintBalance,
    isAmountGreaterThanBalance,
    fromMint,
    toMint,
    filteredWithdrawalAddresses,
    withdrawalAddressStatus,

    // Actions
    handlePercentageClick,
    resetForm,
  }
}
